import clsx from 'clsx';
import IconButton from 'components/IconButton';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { PulseOutline } from 'react-ionicons';
import Divider from 'components/Divider';
import {
  OfferingType,
  TagType,
} from 'screens/AccessExpressV3/accessExpress.types';
import { AccessServiceSummary } from '../AccessServiceSummary';
import DetailsItem from './DetailsItem';
import {
  TermType,
  getServiceActivationFee,
} from 'screens/SelectServices/utils';
import { ProductType } from 'screens/SelectServices/schemas';
import { observer } from 'mobx-react-lite';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { AddToCartButton } from '../AddToCartButton';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { SelectedPackages } from 'context/SingleServiceManagerContext';
import { formatCurrency } from 'shared/util/formatCurrency';

const CuratedOfferDetails = observer(
  ({
    tag,
    isSelected,
    onAddToCart,
    offering,
    serviceSummaryData,
    mns,
    onMNSChange,
  }: {
    tag: TagType;
    isSelected: boolean;
    onAddToCart: (tag?: string) => void;
    offering: OfferingType;
    serviceSummaryData?: (ProductType & { term: TermType }) | undefined;
    mns: SelectedPackages;
    onMNSChange: () => void;
  }) => {
    const { hasCircuitLimitReached } = useCartContext();
    const [expand, setExpand] = useState(false);

    const onExpand = () => setExpand((prev) => !prev);

    const serviceActivationFee = getServiceActivationFee(serviceSummaryData);

    const isDisabled = hasCircuitLimitReached('Broadband', offering.id!);

    const hasMNS = mns.essential || mns.enhanced;

    const pricing =
      serviceSummaryData?.formal_broadband_pricing ||
      serviceSummaryData?.formal_dia_pricing;

    const totalMRC =
      (offering.price ?? 0) +
      (mns.essential
        ? pricing?.mns_essentials ?? 0
        : mns.enhanced
          ? pricing?.mns_enhanced ?? 0
          : 0);

    return (
      <div className="flex w-full flex-wrap items-start justify-between gap-6">
        <div className="flex flex-1 flex-col gap-4">
          <h1
            className={clsx(
              'text-4xl font-bold text-content-base-default',
              tag !== 'Best offer' && '!text-2xl',
            )}
          >
            {offering.carrier}
          </h1>
          <p className="flex flex-col justify-center font-bold">
            <span className="flex h-[38px] items-center gap-1">
              <span className="text-[28px] text-content-base-default">
                {formatCurrency(totalMRC)}
              </span>
              <span className="self-center text-xl text-content-base-subdued">
                /month
              </span>
              <IconButton
                icon={'chevronDown'}
                className={clsx({ 'rotate-180': expand })}
                buttonType="ghost"
                color="currentColor"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onExpand();
                }}
              />
            </span>
            <span className="text-xs">
              +{serviceActivationFee} service activation fee
            </span>
            <motion.div
              className="overflow-hidden"
              initial={{ height: 0, opacity: 0, marginTop: 0 }}
              animate={
                expand
                  ? { height: 'auto', opacity: 1, marginTop: `8px` }
                  : { height: 0, opacity: 0, marginTop: 0 }
              }
              transition={{
                type: 'spring',
                stiffness: 100,
                damping: 15,
              }}
            >
              <Divider className="my-4" />
              <AccessServiceSummary data={serviceSummaryData} mns={mns} />

              <GraniteButton
                variant={hasMNS ? 'secondary' : 'primary'}
                className="mt-6 w-full max-w-[256px] whitespace-nowrap"
                onClick={(e) => {
                  e.stopPropagation();
                  onMNSChange();
                }}
              >
                {hasMNS
                  ? 'Remove network monitoring'
                  : 'Add network monitoring'}
              </GraniteButton>
            </motion.div>
          </p>
          <AddToCartButton
            isSelected={isSelected}
            onAddToCart={onAddToCart}
            tag={tag}
            className="hidden xl:flex"
            disabled={isDisabled}
          />
        </div>
        <div
          className={clsx(
            'flex w-full flex-1 flex-wrap gap-x-2 gap-y-0',
            tag === 'Best offer' && 'xs:min-w-[200px] xs:max-w-[390px] xl:mt-7',
          )}
        >
          {offering.details?.map((item, index) => {
            const showConstructionWarning =
              offering.construction_likely && item.id === 'construction_likely';

            return (
              <div
                key={index}
                className={clsx(
                  'flex-[1_0_calc(33.333%-8px)]',
                  !['speed', 'term'].includes(item.id!) && 'min-w-[160px] ',
                )}
              >
                <DetailsItem
                  {...item}
                  icon={item.icon as React.ElementType}
                  showConstructionWarning={showConstructionWarning}
                />
              </div>
            );
          })}
          {hasMNS && (
            <div className="flex-[1_0_calc(33.333%-8px)]">
              <DetailsItem icon={PulseOutline} text="Network monitoring" />
            </div>
          )}
        </div>
        <AddToCartButton
          isSelected={isSelected}
          onAddToCart={onAddToCart}
          tag={tag}
          className="mt-auto block h-min xl:hidden"
          disabled={isDisabled}
        />
      </div>
    );
  },
);

export default CuratedOfferDetails;
